import React from 'react';

import type { CardVariant } from '../../knowledge-smart-card/knowledge-smart-card';
import { ProjectHighlights } from '../sub-agents/components/project-highlights/project-highlights';
import { RelatedPeople } from '../sub-agents/components/related-people/related-people';
import {
	type ActionElementType,
	type CarouselElementType,
	type MarkdownElementType,
	type ParsedElementType,
	type SmartLinkElementType,
} from '../types';

import { CarouselElement } from './carousel-element';
import { MarkdownElement } from './markdown-element';
import { SmartLinkElement } from './smartlink-element';

export type ElementPosition =
	| 'two-rows-bottom'
	| 'two-rows-top'
	| 'two-columns-left'
	| 'two-columns-right'
	| 'single-column';

interface LuminaSectionElementProps {
	component: ParsedElementType;
	isFoldable?: boolean;
	position: ElementPosition;
	variant?: CardVariant;
}

export const LuminaSectionElement = ({
	component,
	position,
	isFoldable = false,
	variant,
}: LuminaSectionElementProps): React.JSX.Element | null => {
	if (!component || (typeof component !== 'string' && !component.tag)) {
		return null;
	}

	if (typeof component !== 'string') {
		switch (component.tag) {
			case 'md':
				return <MarkdownElement component={component as MarkdownElementType} />;
			case 'smart-link':
				return (
					<SmartLinkElement
						component={component as SmartLinkElementType}
						variant={variant}
						isStandalone
					/>
				);
			case 'carousel':
				return (
					<CarouselElement
						component={component as CarouselElementType}
						position={position}
						isFoldable={isFoldable}
					/>
				);
			case 'action-card': {
				const actionCard = component as ActionElementType;
				// Validate component has a url and convert to smart-link
				if (actionCard.attributes?.url) {
					const smartLinkComponent: SmartLinkElementType = {
						tag: 'smart-link',
						attributes: {
							url: actionCard.attributes.url,
							title: actionCard.attributes?.title,
							type: 'action',
							pos: actionCard.attributes?.pos,
						},
					};
					return <SmartLinkElement component={smartLinkComponent} variant={variant} isStandalone />;
				}
				return null;
			}
			case 'related-people':
				return <RelatedPeople />;
			case 'project-highlights':
				return <ProjectHighlights />;
			default:
				return null;
		}
	}

	return null;
};
